import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../services/axios";
import { toast } from "react-toastify";
import { usePermissions } from "../../context/PermissionsContext";
import { formatAppDateFromKey, formatAppDateRange, formatAppDateTime } from "../../utils/dateFormat";
import ClientAnalyticsSection from "./ClientAnalyticsSection";
import JdAnalyticsSection from "./JdAnalyticsSection";
import {
  FileText, UserCheck, Percent, ThumbsUp, ThumbsDown,
  Calendar, RefreshCw, Sparkles, MessageSquareReply, Download, ChevronDown, Info, X, Mail, Globe,
  Send, Clock, Bookmark, FileEdit, Building2,
} from "lucide-react";
import {
  uniqueTenantMembers,
  normalizeSsoEmail,
  positionsForSsoRecruiters,
  positionsForTenants,
} from "../../utils/recruiterSsoFilter";
import RecruiterSsoDropdown from "../../components/custom/RecruiterSsoDropdown";

/* ── helpers ─────────────────────────────────────────────────────────────── */
function toIsoDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function clampIsoDate(value, minStr, maxStr) {
  if (!value) return value;
  let next = value;
  if (minStr && next < minStr) next = minStr;
  if (maxStr && next > maxStr) next = maxStr;
  return next;
}
function getDateRangeError(fromStr, toStr) {
  if (!fromStr || !toStr) return "Please select both From and To dates.";
  if (fromStr > toStr) return "To date must be on or after the From date.";
  return "";
}
function getFromFieldError(fromStr, toStr) {
  if (!fromStr) return "";
  if (toStr && fromStr > toStr) return "From date cannot be after the To date.";
  return "";
}
function getToFieldError(fromStr, toStr, todayStr) {
  if (!toStr) return "";
  if (todayStr && toStr > todayStr) return "To date cannot be in the future.";
  if (fromStr && toStr < fromStr) return "To date must be on or after the From date.";
  return "";
}

/* ── shared style tokens ──────────────────────────────────────────────────── */
const card  = { background: "var(--ds-card)",   border: "1px solid var(--ds-border)", borderRadius: 12, boxShadow: "var(--ds-shadow-md)" };
const card2 = { background: "var(--ds-card2)",  border: "1px solid var(--ds-border)", borderRadius: 12, boxShadow: "var(--ds-shadow-md)" };
const inp   = {
  background: "var(--ds-input-bg)", border: "1.5px solid var(--ds-border)",
  borderRadius: 8, color: "var(--ds-text)", padding: "8px 12px",
  fontSize: 13, outline: "none", width: "100%", fontFamily: "inherit",
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: "var(--ds-text2)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 };
const btnPrimary = {
  background: "linear-gradient(135deg,#0041C2,#1A5FD4)", color: "#fff",
  border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600,
  cursor: "pointer", fontSize: 13, fontFamily: "inherit", display: "inline-flex",
  alignItems: "center", gap: 6,
};
const btnOutline = {
  background: "var(--ds-input-bg)", border: "1.5px solid var(--ds-border)",
  color: "var(--ds-text2)", borderRadius: 8, padding: "8px 14px",
  fontWeight: 600, cursor: "pointer", fontSize: 12, fontFamily: "inherit",
};
const KPI_VISIBLE_COUNT = 4;
const filterChipBase = {
  display: "inline-flex", alignItems: "center", gap: 4,
  borderRadius: 20, padding: "3px 8px 3px 10px", fontSize: 11, fontWeight: 600,
};
const filterChipClearBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 16, height: 16, padding: 0, border: "none", borderRadius: "50%",
  background: "transparent", cursor: "pointer", opacity: 0.75, transition: "opacity 0.15s",
};

/* ── KPI card ─────────────────────────────────────────────────────────────── */
const KpiCard = ({ title, value, icon: Icon, accent, hint, dateRange }) => (
  <div style={{ ...card, padding: 20, position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 5, background: accent, borderRadius: "12px 0 0 12px" }} />
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
      <div>
        <div style={{ color: "var(--ds-text2)", fontSize: 12, fontWeight: 800 }}>{title}</div>
        <div style={{ fontSize: 34, fontWeight: 900, color: "var(--ds-text)", marginTop: 6 }}>{value}</div>
      </div>
      <div
        style={{ width: 40, height: 40, borderRadius: 12, background: `${accent}22`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        title={hint}
      >
        <Icon size={20} />
      </div>
    </div>
    {dateRange && (
      <div style={{ color: "var(--ds-muted)", fontSize: 11, marginTop: 10 }}>{dateRange}</div>
    )}
  </div>
);

/* ── SVG Donut chart ─────────────────────────────────────────────────────── */
function DonutChart({ slices, size = 130, thickness = 20 }) {
  const r    = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  const cx   = size / 2;
  const cy   = size / 2;
  const total = slices.reduce((s, d) => s + d.value, 0);

  let accumulated = 0;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      {total === 0 ? (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--ds-border)" strokeWidth={thickness} />
      ) : (
        slices.map((s, i) => {
          const pct   = s.value / total;
          const dash  = pct * circ;
          const offset = circ * 0.25 - accumulated;
          accumulated += dash;
          return (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={s.color} strokeWidth={thickness}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: "stroke-dasharray 0.5s ease" }}
            />
          );
        })
      )}
    </svg>
  );
}

/* ── zero/aggregate response ──────────────────────────────────────────────── */
const analyticsSessionCache = {
  data: null,
  filtersSaved: false,
  selectedRecruiters: [],
  selectedPositions: [],
  tenantIds: [],
  from: null,
  to: null,
};

function tenantQuery(tenantIds) {
  const list = tenantIds instanceof Set
    ? Array.from(tenantIds).filter(Boolean)
    : Array.isArray(tenantIds) ? tenantIds.filter(Boolean) : [];
  return list.length ? { tenantIds: list.join(",") } : {};
}

function tenantPickerOptions(tenants) {
  return (tenants || []).map((t) => ({ email: String(t.id), name: t.name }));
}

const ZERO_DATA = (from, to) => ({
  from, to,
  jobDescriptionsCreated: 0, stackRankingRuns: 0, resumesAnalyzed: 0,
  resumesScoredAboveFiftyPercent: 0, candidatesInterested: 0,
  candidatesNotInterested: 0, candidatesNoResponse: 0, candidatesShortlisted: 0,
  outreachRecipients: 0, overallResponseRatePercent: 0,
});

/* ── component ───────────────────────────────────────────────────────────── */
export default function AnalyticsDashboard() {
  const perms = usePermissions();
  const defaultTo   = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; }, []);

  const todayMaxDate              = useMemo(() => toIsoDate(new Date()), []);
  const [from, setFrom]           = useState(() => analyticsSessionCache.from || toIsoDate(defaultFrom));
  const [to, setTo]               = useState(() => analyticsSessionCache.to || toIsoDate(defaultTo));
  const dateRangeError            = useMemo(() => getDateRangeError(from, to), [from, to]);
  const fromFieldError            = useMemo(() => getFromFieldError(from, to), [from, to]);
  const toFieldError              = useMemo(() => getToFieldError(from, to, todayMaxDate), [from, to, todayMaxDate]);
  const fromMaxDate               = to || undefined;
  const toMinDate                 = from || undefined;
  const [positions, setPositions] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState(
    () => new Set(analyticsSessionCache.filtersSaved ? analyticsSessionCache.selectedPositions : [])
  );
  const [positionOpen, setPositionOpen]             = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [data, setData]           = useState(() => analyticsSessionCache.data);

  // Recruiter filter
  const [selectedRecruiters, setSelectedRecruiters] = useState(
    () => new Set(analyticsSessionCache.filtersSaved ? analyticsSessionCache.selectedRecruiters : [])
  );
  const [tenantOptions, setTenantOptions] = useState([]);
  const [tenantMembers, setTenantMembers] = useState([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState(
    () => new Set(analyticsSessionCache.filtersSaved ? analyticsSessionCache.tenantIds : [])
  );
  const [downloading, setDownloading]               = useState(false);
  const [exportOpen, setExportOpen]                 = useState(false);
  const [exportMsg, setExportMsg]                   = useState("");
  const [exportMenuPos, setExportMenuPos]           = useState(null);
  const positionRef                                 = useRef(null);
  const exportBtnRef                                = useRef(null);
  const exportMenuRef                               = useRef(null);

  const analyticsRequestIdRef = useRef(0);
  const analyticsAbortRef = useRef(null);
  const filtersHydratedRef = useRef(false);

  useEffect(() => {
    if (!filtersHydratedRef.current) return;
    analyticsSessionCache.filtersSaved = true;
    analyticsSessionCache.selectedRecruiters = Array.from(selectedRecruiters);
    analyticsSessionCache.selectedPositions = Array.from(selectedPositions);
    analyticsSessionCache.tenantIds = Array.from(selectedTenantIds);
    analyticsSessionCache.from = from;
    analyticsSessionCache.to = to;
  }, [selectedRecruiters, selectedPositions, selectedTenantIds, from, to]);

  const refreshAnalytics = useCallback(async (opts = {}) => {
    const nextFrom       = opts.from               ?? from;
    const nextTo         = opts.to                 ?? to;
    const nextPositions  = opts.selectedPositions  ?? selectedPositions;
    const nextRecruiters = opts.selectedRecruiters ?? selectedRecruiters;
    const nextTenantIds  = opts.selectedTenantIds  ?? selectedTenantIds;

    const rangeErr = getDateRangeError(nextFrom, nextTo);
    if (rangeErr) {
      setError(rangeErr);
      setData(null);
      return;
    }

    if (analyticsAbortRef.current) {
      analyticsAbortRef.current.abort();
    }
    const controller = new AbortController();
    analyticsAbortRef.current = controller;
    const requestId = ++analyticsRequestIdRef.current;

    setLoading(true);
    setError("");

    const summaryParams = { from: nextFrom, to: nextTo, ...tenantQuery(nextTenantIds) };
    if (nextRecruiters.size > 0) {
      summaryParams.recruiterEmails = Array.from(nextRecruiters).join(",");
    }
    if (nextPositions.size > 0) {
      summaryParams.positionIds = Array.from(nextPositions).join(",");
    }

    try {
      // Single source of truth for all 10 KPI cards + insights/charts
      const summaryRes = await api.get("/analytics/summary", {
        params: summaryParams,
        signal: controller.signal,
      });

      if (requestId !== analyticsRequestIdRef.current) return;

      analyticsSessionCache.data = summaryRes.data;
      setData(summaryRes.data);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (requestId !== analyticsRequestIdRef.current) return;
      const msg = e?.response?.data?.message || e?.response?.data?.detail || e?.message || "Failed to load analytics.";
      setError(String(msg));
      setData(null);
    } finally {
      if (requestId === analyticsRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [from, to, selectedPositions, selectedRecruiters, selectedTenantIds]);

  // Info popup
  const [infoOpen, setInfoOpen] = useState(false);
  const [showAllKpis, setShowAllKpis] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const infoRef                 = useRef(null);

  useEffect(() => {
    if (!infoOpen) return;
    const handler = (e) => { if (infoRef.current && !infoRef.current.contains(e.target)) setInfoOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [infoOpen]);

  /* Tenant first, then roles from those jobs. Recruiter names come from membership. */
  const tenantScopedPositions = useMemo(
    () => positionsForTenants(positions, selectedTenantIds),
    [positions, selectedTenantIds]
  );
  const recruiters = useMemo(
    () => uniqueTenantMembers(tenantMembers, selectedTenantIds),
    [tenantMembers, selectedTenantIds]
  );
  const rolePositions = useMemo(
    () => positionsForSsoRecruiters(tenantScopedPositions, selectedRecruiters),
    [tenantScopedPositions, selectedRecruiters]
  );

  // Load positions; restore saved filters. First visit defaults to all recruiters (no SSO pre-filter).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      let options = [];
      let members = [];
      try {
        const tRes = await api.get("/analytics/tenants");
        const list = Array.isArray(tRes.data?.tenants) ? tRes.data.tenants : [];
        options = list.filter((t) => t && t.id != null);
        members = Array.isArray(tRes.data?.members) ? tRes.data.members : [];
      } catch {
        options = [];
        members = [];
      }
      if (cancelled) return;
      setTenantOptions(options);
      setTenantMembers(members);

      const allowed = new Set(options.map((t) => String(t.id)));
      const cachedIds = (analyticsSessionCache.tenantIds || []).map(String).filter((id) => allowed.has(id));
      const initialTenantIds = new Set(cachedIds);
      setSelectedTenantIds(initialTenantIds);

      try {
        const posRes = await api.get("/analytics/positions", { params: tenantQuery(initialTenantIds) });
        if (cancelled) return;

        const posList = Array.isArray(posRes.data) ? posRes.data : [];
        setPositions(posList);

        let initialRecruiters;
        let initialPositions;

        if (analyticsSessionCache.filtersSaved) {
          const scoped = positionsForTenants(posList, initialTenantIds);
          const allowedRecruiters = new Set(
            uniqueTenantMembers(members, initialTenantIds).map((r) => r.email)
          );
          initialRecruiters = new Set(
            (analyticsSessionCache.selectedRecruiters || [])
              .map(normalizeSsoEmail)
              .filter((email) => allowedRecruiters.has(email))
          );
          const allowedPos = new Set(
            positionsForSsoRecruiters(scoped, initialRecruiters).map((p) => String(p.positionId))
          );
          initialPositions = new Set(
            (analyticsSessionCache.selectedPositions || []).filter((id) => allowedPos.has(String(id)))
          );
        } else {
          initialRecruiters = new Set();
          initialPositions = new Set();
          analyticsSessionCache.filtersSaved = true;
          analyticsSessionCache.selectedRecruiters = [];
          analyticsSessionCache.selectedPositions = [];
          analyticsSessionCache.tenantIds = Array.from(initialTenantIds);
        }

        const initialFrom = analyticsSessionCache.from || from;
        const initialTo = analyticsSessionCache.to || to;

        setSelectedRecruiters(initialRecruiters);
        setSelectedPositions(initialPositions);
        if (analyticsSessionCache.from) setFrom(analyticsSessionCache.from);
        if (analyticsSessionCache.to) setTo(analyticsSessionCache.to);

        filtersHydratedRef.current = true;

        await refreshAnalytics({
          from: initialFrom,
          to: initialTo,
          selectedPositions: initialPositions,
          selectedRecruiters: initialRecruiters,
          selectedTenantIds: initialTenantIds,
        });
      } catch {
        if (!cancelled) {
          setPositions([]);
          filtersHydratedRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
      if (analyticsAbortRef.current) analyticsAbortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTenantChange = (nextIds) => {
    const ids = nextIds instanceof Set ? nextIds : new Set();
    setSelectedTenantIds(ids);
    (async () => {
      let posList = [];
      try {
        const posRes = await api.get("/analytics/positions", { params: tenantQuery(ids) });
        posList = Array.isArray(posRes.data) ? posRes.data : [];
      } catch {
        posList = [];
      }
      setPositions(posList);
      const scoped = positionsForTenants(posList, ids);
      const allowedRecruiters = new Set(uniqueTenantMembers(tenantMembers, ids).map((r) => r.email));
      const nextRecruiters = new Set(
        Array.from(selectedRecruiters).filter((email) => allowedRecruiters.has(normalizeSsoEmail(email)))
      );
      const allowedPos = new Set(
        positionsForSsoRecruiters(scoped, nextRecruiters).map((p) => String(p.positionId))
      );
      const nextPositions = new Set(
        Array.from(selectedPositions).filter((id) => allowedPos.has(String(id)))
      );
      setSelectedRecruiters(nextRecruiters);
      setSelectedPositions(nextPositions);
      if (!dateRangeError) {
        refreshAnalytics({
          from, to,
          selectedPositions: nextPositions,
          selectedRecruiters: nextRecruiters,
          selectedTenantIds: ids,
        });
      }
    })();
  };

  const handleRecruiterChange = (nextRecruiters) => {
    const next = nextRecruiters instanceof Set ? nextRecruiters : new Set();
    const allowedPos = new Set(
      positionsForSsoRecruiters(tenantScopedPositions, next).map((p) => String(p.positionId))
    );
    const nextPositions = new Set(
      Array.from(selectedPositions).filter((id) => allowedPos.has(String(id)))
    );
    setSelectedRecruiters(next);
    setSelectedPositions(nextPositions);
    if (!dateRangeError) {
      refreshAnalytics({
        from, to,
        selectedPositions: nextPositions,
        selectedRecruiters: next,
        selectedTenantIds,
      });
    }
  };

  /* Close position dropdown on outside click */
  useEffect(() => {
    if (!positionOpen) return;
    const handler = (e) => {
      if (positionRef.current && !positionRef.current.contains(e.target))
        setPositionOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [positionOpen]);

  const updateExportMenuPos = useCallback(() => {
    const btn = exportBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setExportMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
  }, []);

  useEffect(() => {
    if (!exportOpen) {
      setExportMenuPos(null);
      return;
    }
    updateExportMenuPos();
    window.addEventListener("scroll", updateExportMenuPos, true);
    window.addEventListener("resize", updateExportMenuPos);
    return () => {
      window.removeEventListener("scroll", updateExportMenuPos, true);
      window.removeEventListener("resize", updateExportMenuPos);
    };
  }, [exportOpen, updateExportMenuPos]);

  useEffect(() => {
    if (!exportOpen) return;
    const handler = (e) => {
      if (exportBtnRef.current?.contains(e.target) || exportMenuRef.current?.contains(e.target)) return;
      setExportOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  const applyPreset = (days) => {
    const t = new Date(); const f = new Date(); f.setDate(f.getDate() - days);
    const fromStr = toIsoDate(f); const toStr = toIsoDate(t);
    setFrom(fromStr); setTo(toStr);
    refreshAnalytics({ from: fromStr, to: toStr, selectedPositions, selectedRecruiters });
  };
  const handleFromChange = (rawFrom) => {
    if (!rawFrom) { setFrom(""); return; }
    const nextFrom = clampIsoDate(rawFrom, "", fromMaxDate || "");
    setFrom(nextFrom); setTo((prevTo) => (prevTo ? clampIsoDate(prevTo, nextFrom, "") : prevTo));
    const rangeErr = getDateRangeError(nextFrom, to);
    if (!rangeErr) {
      refreshAnalytics({ from: nextFrom, to, selectedPositions, selectedRecruiters });
    }
  };
  const handleToChange = (rawTo) => {
    if (!rawTo) { setTo(""); return; }
    const nextTo = clampIsoDate(rawTo, from, todayMaxDate);
    setTo(nextTo);
    const rangeErr = getDateRangeError(from, nextTo);
    if (!rangeErr) {
      refreshAnalytics({ from, to: nextTo, selectedPositions, selectedRecruiters });
    }
  };

  const removePositionFilter = (id) => {
    const next = new Set(selectedPositions);
    next.delete(String(id));
    setSelectedPositions(next);
    if (!dateRangeError) {
      refreshAnalytics({ from, to, selectedPositions: next, selectedRecruiters });
    }
  };

  const removeRecruiterFilter = (email) => {
    const next = new Set(selectedRecruiters);
    next.delete(email);
    handleRecruiterChange(next);
  };
  const safeNum                = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  const jdCount                = safeNum(data?.jobDescriptionsCreated);
  const runs                   = safeNum(data?.stackRankingRuns);
  const resumesAnalyzed        = safeNum(data?.resumesAnalyzed);
  const candidatesSelfRanked   = resumesAnalyzed;
  const resumesScoredAboveFifty = safeNum(data?.resumesScoredAboveFiftyPercent);
  const resumesFormatted       = safeNum(data?.resumesFormatted);
  // Invitation-cohort WhatsApp metrics — all derived from the same /analytics/summary payload
  const invitationsSent        = safeNum(data?.outreachRecipients);
  const interested             = safeNum(data?.candidatesInterested);
  const notInterested          = safeNum(data?.candidatesNotInterested);
  const noResponse             = data != null
    ? safeNum(data.candidatesNoResponse ?? Math.max(0, invitationsSent - interested - notInterested))
    : 0;
  const shortlisted            = safeNum(data?.candidatesShortlisted);
  const totalResponses         = interested + notInterested;
  const avgResumesPerRun       = runs > 0 ? resumesAnalyzed / runs : 0;
  const interestRate           = totalResponses > 0 ? (interested / totalResponses) * 100 : 0;
  const overallResponseRatePct = (() => {
    if (data == null) return 0;
    const raw = data.overallResponseRatePercent;
    if (raw == null) {
      return invitationsSent > 0
        ? Math.round(10 * Math.min(100, (100 * totalResponses) / invitationsSent)) / 10
        : 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? Math.min(100, n) : 0;
  })();

  const dateRange = data?.from && data?.to ? formatAppDateRange(data.from, data.to) : null;
  const displayPeriod = formatAppDateRange(from, to);

  const displayValue = (value) => (value != null ? value : (loading ? "…" : "-"));

  const kpis = [
    {
      title: "Job roles created",
      value: displayValue(data != null ? jdCount : null),
      icon: FileText,
      accent: "#ff6b35",
      hint: selectedRecruiters.size > 0
        ? "Positions created by the selected user(s) whose Created On date falls in the range"
        : selectedPositions.size > 0
          ? "Selected role(s) whose Created On date falls in the range"
          : "Active job roles whose Created On date falls in the range",
    },
    { title: "Candidates Stack Ranked", value: displayValue(data != null ? candidatesSelfRanked : null), icon: UserCheck, accent: "#2f80ed", hint: "Total candidates whose resumes were processed through stack ranking in the range" },
    { title: "Resume scoring above 50%", value: displayValue(data != null ? resumesScoredAboveFifty : null), icon: Percent, accent: "#27ae60", hint: "Resumes scoring 50%+ on each role's latest run in the range" },
    { title: "Resumes Formatted", value: displayValue(data != null ? resumesFormatted : null), icon: FileEdit, accent: "#0ea5e9", hint: "Resumes successfully formatted through the Resume Formatter tool in the range" },
    { title: "WhatsApp Invitation Sent", value: displayValue(data != null ? invitationsSent : null), icon: Send, accent: "#f59e0b", hint: "Distinct (candidate, role) pairs who received the initial WhatsApp template in the selected date range" },
    { title: "Interested", value: displayValue(data != null ? interested : null), icon: ThumbsUp, accent: "#16a34a", hint: "Among invites in range — candidates who replied Yes / progressed as Interested" },
    { title: "Not Interested", value: displayValue(data != null ? notInterested : null), icon: ThumbsDown, accent: "#ef4444", hint: "Among invites in range — candidates who replied No" },
    { title: "No Response", value: displayValue(data != null ? noResponse : null), icon: Clock, accent: "#6b7280", hint: "Invites in range with no Yes/No reply yet (Invitations − Interested − Not Interested)" },
    { title: "Shortlisted", value: displayValue(data != null ? shortlisted : null), icon: Bookmark, accent: "#7c3aed", hint: "Invites in range that were marked Shortlisted by a recruiter" },
    { title: "Overall response rate", value: displayValue(data != null ? `${overallResponseRatePct.toFixed(1)}%` : null), icon: MessageSquareReply, accent: "#7c3aed", hint: "(Interested + Not Interested) ÷ WhatsApp invitations sent in the same date range" },
  ];

  const presets = [{ label: "Today", days: 0 }, { label: "Last 7 days", days: 7 }, { label: "Last 30 days", days: 30 }];

  /* Role label for dropdown button */
  const positionLabel = selectedPositions.size === 0
    ? "All roles"
    : selectedPositions.size === 1
      ? (rolePositions.find((p) => selectedPositions.has(String(p.positionId)))?.positionName || "1 selected")
      : `${selectedPositions.size} roles`;

  /* ── Export helpers ─────────────────────────────────────────────────────── */
  const buildReportMeta = () => {
    const filterParts = [];
    if (selectedPositions.size > 0) {
      const names = Array.from(selectedPositions)
        .map((id) => positions.find((p) => String(p.positionId) === String(id))?.positionName || id)
        .join(", ");
      filterParts.push(`Position(s): ${names}`);
    }
    if (selectedRecruiters.size > 0) {
      const names = Array.from(selectedRecruiters).map((e) => recruiters.find((r) => r.email === e)?.name || e).join(", ");
      filterParts.push(`Recruiter(s): ${names}`);
    }
    if (selectedTenantIds.size > 0) {
      const names = Array.from(selectedTenantIds)
        .map((id) => tenantOptions.find((t) => String(t.id) === String(id))?.name || id)
        .join(", ");
      filterParts.push(`Tenant(s): ${names}`);
    }
    if (selectedPositions.size === 0 && selectedRecruiters.size === 0) filterParts.push("All roles & users");
    return {
      filterLabel: filterParts.join(" | "),
      filterBadges: filterParts.map((f) => `<span style="display:inline-block;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:20px;padding:2px 10px;font-size:11px;font-weight:600;margin-right:6px;">${f}</span>`).join(""),
      nowStr: formatAppDateTime(),
      rows: [
        ["Job Roles / JDs Created", data ? jdCount : 0],
        ["Candidates Stack Ranked", data ? candidatesSelfRanked : 0],
        ["Resumes Scored > 50%", data ? resumesScoredAboveFifty : 0],
        ["Resumes Formatted", data ? resumesFormatted : 0],
        ["WhatsApp Invitation Sent", data ? invitationsSent : 0],
        ["Candidates Interested", data ? interested : 0],
        ["Not Interested", data ? notInterested : 0],
        ["No Response", data ? noResponse : 0],
        ["Shortlisted", data ? shortlisted : 0],
        ["Overall Response Rate (%)", data ? overallResponseRatePct.toFixed(1) : 0],
        ["", ""],
        ["Total Candidate Responses", data ? totalResponses : 0],
        ["Avg Resumes per Run", data ? avgResumesPerRun.toFixed(1) : 0],
        ["Interest Rate (%)", data ? interestRate.toFixed(1) : 0],
      ],
    };
  };

  const downloadBlob = (content, filename, type) => {
    if (!perms.can("analytics.export")) {
      toast.warn("You don't have permission to export reports.");
      return;
    }
    const blob = new Blob([content], { type });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    const { filterLabel, rows } = buildReportMeta();
    const csvRows = [
      ["Analytics Report"],
      ["Period", displayPeriod || `${formatAppDateFromKey(from)} to ${formatAppDateFromKey(to)}`],
      ["Filter", filterLabel],
      ["Generated", formatAppDateTime()],
      [],
      ["Metric", "Value"],
      ...rows,
    ];
    const csv = csvRows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    downloadBlob(csv, `analytics-report-${from}-to-${to}.csv`, "text/csv;charset=utf-8;");
    setExportOpen(false);
  };

  const exportToExcel = () => {
    const { filterLabel, rows } = buildReportMeta();
    const tableRows = rows.map(([m, v]) =>
      m ? `<tr><td>${m}</td><td><b>${v}</b></td></tr>` : "<tr><td colspan='2'></td></tr>"
    ).join("");
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"/><style>table{border-collapse:collapse;}th,td{border:1px solid #ccc;padding:6px 10px;font-size:12px;}th{background:#dbeafe;font-weight:700;}.hdr{background:#1e40af;color:#fff;font-weight:800;}</style></head><body>
<table>
<tr><td class="hdr" colspan="2">Analytics Report</td></tr>
<tr><th>Period</th><td>${displayPeriod || `${formatAppDateFromKey(from)} to ${formatAppDateFromKey(to)}`}</td></tr>
<tr><th>Filter</th><td>${filterLabel}</td></tr>
<tr><th>Generated</th><td>${formatAppDateTime()}</td></tr>
<tr><td colspan="2"></td></tr>
<tr><td class="hdr" colspan="2">Metrics</td></tr>
<tr><th>Metric</th><th>Value</th></tr>
${tableRows}
</table></body></html>`;
    downloadBlob(html, `analytics-report-${from}-to-${to}.xls`, "application/vnd.ms-excel;charset=utf-8;");
    setExportOpen(false);
  };

  const exportToGoogleSheets = async () => {
    const { filterLabel, rows } = buildReportMeta();
    const tsvRows = [
      ["Analytics Report", ""],
      ["Period", displayPeriod || `${formatAppDateFromKey(from)} to ${formatAppDateFromKey(to)}`],
      ["Filter", filterLabel],
      ["Generated", formatAppDateTime()],
      ["", ""],
      ["Metric", "Value"],
      ...rows,
    ];
    const tsv = tsvRows.map((r) => r.join("\t")).join("\n");
    try { await navigator.clipboard.writeText(tsv); } catch {}
    setExportMsg("Data copied to clipboard — paste it into the new sheet (Ctrl+V).");
    setTimeout(() => setExportMsg(""), 5000);
    window.open("https://sheets.new", "_blank");
    setExportOpen(false);
  };

  const exportToHTML = () => {
    setDownloading(true);
    const { filterBadges } = buildReportMeta();

    const nowStr = formatAppDateTime();
    const periodLabel = displayPeriod || formatAppDateRange(from, to);

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Analytics Report – ${periodLabel}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;padding:36px 40px;font-size:13px;line-height:1.5;background:#fff;}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;}
    h1{font-size:24px;font-weight:800;color:#0041C2;margin-bottom:4px;}
    .meta{color:#6b7280;font-size:12px;margin-bottom:24px;}
    .print-btn{padding:8px 18px;background:#0041C2;color:#fff;border:none;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;}
    .section{margin-bottom:30px;}
    .section-title{font-size:13px;font-weight:800;color:#0041C2;text-transform:uppercase;letter-spacing:0.06em;border-bottom:2px solid #dbeafe;padding-bottom:6px;margin-bottom:16px;}
    .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}
    .kpi{border:1px solid #e5e7eb;border-radius:10px;padding:16px 18px;border-left:4px solid;}
    .kpi-label{font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.07em;margin-bottom:8px;}
    .kpi-value{font-size:30px;font-weight:900;line-height:1;}
    table{width:100%;border-collapse:collapse;}
    th{background:#f1f5f9;padding:10px 14px;text-align:left;font-size:11px;font-weight:700;color:#475569;border:1px solid #e2e8f0;text-transform:uppercase;letter-spacing:0.05em;}
    td{padding:10px 14px;border:1px solid #e2e8f0;font-size:13px;vertical-align:middle;}
    tr:nth-child(even) td{background:#f8fafc;}
    .pill{display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:700;}
    .bar-wrap{height:8px;background:#e5e7eb;border-radius:999px;overflow:hidden;width:120px;display:inline-block;vertical-align:middle;margin-left:8px;}
    .bar-fill{height:100%;border-radius:999px;}
    .footer{margin-top:40px;padding-top:14px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:11px;display:flex;justify-content:space-between;}
    @media print{.print-btn{display:none;}body{padding:20px 24px;}}
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>Analytics Report</h1>
      <div class="meta">Period: <strong>${periodLabel}</strong>&nbsp;&nbsp;|&nbsp;&nbsp;Generated: ${nowStr}</div>
      <div style="margin-bottom:20px;">${filterBadges}</div>
    </div>
    <button class="print-btn" onclick="window.print()">Print / Save PDF</button>
  </div>

  <div class="section">
    <div class="section-title">Key Performance Indicators</div>
    <div class="kpi-grid">
      <div class="kpi" style="border-left-color:#ff6b35;">
        <div class="kpi-label">${selectedPositions.size > 0 ? "JDs Created" : "Job Roles Created"}</div>
        <div class="kpi-value" style="color:#ff6b35;">${data ? jdCount : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#2f80ed;">
        <div class="kpi-label">Candidates Stack Ranked</div>
        <div class="kpi-value" style="color:#2f80ed;">${data ? candidatesSelfRanked : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#27ae60;">
        <div class="kpi-label">Resumes Scored &gt; 50%</div>
        <div class="kpi-value" style="color:#27ae60;">${data ? resumesScoredAboveFifty : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#f59e0b;">
        <div class="kpi-label">WhatsApp Invitation Sent</div>
        <div class="kpi-value" style="color:#f59e0b;">${data ? invitationsSent : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#16a34a;">
        <div class="kpi-label">Candidates Interested</div>
        <div class="kpi-value" style="color:#16a34a;">${data ? interested : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#ef4444;">
        <div class="kpi-label">Not Interested</div>
        <div class="kpi-value" style="color:#ef4444;">${data ? notInterested : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#6b7280;">
        <div class="kpi-label">No Response</div>
        <div class="kpi-value" style="color:#6b7280;">${data ? noResponse : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#7c3aed;">
        <div class="kpi-label">Shortlisted</div>
        <div class="kpi-value" style="color:#7c3aed;">${data ? shortlisted : "—"}</div>
      </div>
      <div class="kpi" style="border-left-color:#7c3aed;">
        <div class="kpi-label">Overall Response Rate</div>
        <div class="kpi-value" style="color:#7c3aed;">${data ? overallResponseRatePct.toFixed(1) + "%" : "—"}</div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Insights Summary</div>
    <table>
      <thead>
        <tr><th>Metric</th><th>Value</th><th style="width:55%">Details</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Total Candidate Responses</td>
          <td><strong style="font-size:15px;">${data ? totalResponses : "—"}</strong></td>
          <td style="color:#6b7280;">Interested (${data ? interested : "—"}) + Not Interested (${data ? notInterested : "—"})</td>
        </tr>
        <tr>
          <td>Avg Resumes per Ranking Run</td>
          <td><strong style="font-size:15px;">${data ? avgResumesPerRun.toFixed(1) : "—"}</strong></td>
          <td style="color:#6b7280;">Total resumes analyzed (${data ? resumesAnalyzed : "—"}) ÷ Runs (${data ? runs : "—"})</td>
        </tr>
        <tr>
          <td>Interest Rate (from responses)</td>
          <td><strong style="font-size:15px;">${data ? interestRate.toFixed(1) + "%" : "—"}</strong>
            <div class="bar-wrap"><div class="bar-fill" style="width:${Math.min(100, interestRate).toFixed(0)}%;background:linear-gradient(90deg,#16a34a,#22c55e);"></div></div>
          </td>
          <td style="color:#6b7280;">Interested ÷ (Interested + Not Interested)</td>
        </tr>
        <tr>
          <td>Overall Response Rate</td>
          <td><strong style="font-size:15px;">${data ? overallResponseRatePct.toFixed(1) + "%" : "—"}</strong>
            <div class="bar-wrap"><div class="bar-fill" style="width:${Math.min(100, overallResponseRatePct).toFixed(0)}%;background:linear-gradient(90deg,#7c3aed,#a78bfa);"></div></div>
          </td>
          <td style="color:#6b7280;">Responses ÷ Initial template recipients (${invitationsSent})</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="footer">
    <span>Feuji ReachOut &nbsp;|&nbsp; Analytics Report</span>
    <span>${nowStr}</span>
  </div>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => { setDownloading(false); }, 400);
    } else {
      setDownloading(false);
    }
    setExportOpen(false);
  };

  const emailReport = () => {
    const { filterLabel, rows } = buildReportMeta();
    const periodLabel = displayPeriod || formatAppDateRange(from, to);
    const subject = `Analytics Report: ${periodLabel}`;
    const body = [
      "Analytics Report",
      `Period: ${periodLabel}`,
      `Filter: ${filterLabel}`,
      `Generated: ${formatAppDateTime()}`,
      "",
      "METRICS",
      ...rows.map(([m, v]) => (m ? `${m}: ${v}` : "")),
    ].join("\n");
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setExportOpen(false);
  };

  return (
    <div style={{ padding: "24px", fontFamily: "'Plus Jakarta Sans','Inter',sans-serif", color: "var(--ds-text)" }}>
      <style>{`@keyframes ads-spin{to{transform:rotate(360deg)}} .ads-spin{animation:ads-spin 0.7s linear infinite}`}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontWeight: 900, color: "var(--ds-text)", fontSize: 22 }}>Analytics</h3>
        <span style={{ background: "var(--ds-card2)", border: "1px solid var(--ds-border)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "var(--ds-text2)" }}>Dashboard</span>
        {Array.from(selectedPositions).map((id) => {
          const pos = positions.find((p) => String(p.positionId) === String(id));
          return (
            <span key={id} style={{ ...filterChipBase, background: "rgba(0,65,194,0.12)", border: "1px solid rgba(0,65,194,0.25)", color: "#4D86E8" }}>
              {pos?.positionName || `Position ${id}`}
              <button
                type="button"
                onClick={() => removePositionFilter(id)}
                title="Remove role filter"
                style={{ ...filterChipClearBtn, color: "#4D86E8" }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.75"; }}
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        {Array.from(selectedRecruiters).map((email) => {
          const recruiter = recruiters.find((r) => r.email === email);
          return (
            <span key={email} style={{ ...filterChipBase, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)", color: "#059669" }}>
              {recruiter?.name || email}
              <button
                type="button"
                onClick={() => removeRecruiterFilter(email)}
                title="Remove user filter"
                style={{ ...filterChipClearBtn, color: "#059669" }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.75"; }}
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        {Array.from(selectedTenantIds).map((id) => (
          <span key={id} style={{ ...filterChipBase, background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.3)", color: "#7c3aed" }}>
            {tenantOptions.find((t) => String(t.id) === String(id))?.name || id}
            <button
              type="button"
              onClick={() => {
                const next = new Set(selectedTenantIds);
                next.delete(String(id));
                handleTenantChange(next);
              }}
              title="Remove tenant filter"
              style={{ ...filterChipClearBtn, color: "#7c3aed" }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.75"; }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        {selectedPositions.size === 0 && selectedRecruiters.size === 0 && (
          <span style={{ background: "var(--ds-card2)", border: "1px solid var(--ds-border)", color: "var(--ds-muted)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>All roles</span>
        )}
      </div>
      <div style={{ color: "var(--ds-text2)", fontSize: 13, marginBottom: 16 }}>Quick view of usage + outcomes for your chosen date range.</div>

      {/* ── View tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[
          { id: "overview", label: "Overview" },
          { id: "client", label: "Client Analytics" },
          { id: "jd", label: "JD Analytics" },
        ].map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...btnOutline,
                borderColor: isActive ? "#0041C2" : "var(--ds-border)",
                background: isActive ? "rgba(0,65,194,0.1)" : "var(--ds-input-bg)",
                color: isActive ? "#0041C2" : "var(--ds-text2)",
                fontWeight: isActive ? 700 : 600,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "client" ? (
        <ClientAnalyticsSection
          selectedTenantIds={selectedTenantIds}
          tenantOptions={tenantOptions}
          onTenantChange={handleTenantChange}
        />
      ) : activeTab === "jd" ? (
        <JdAnalyticsSection
          selectedTenantIds={selectedTenantIds}
          tenantOptions={tenantOptions}
          onTenantChange={handleTenantChange}
        />
      ) : (
      <>

      {/* ── Filters card ── */}
      <div style={{ ...card, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>

          {tenantOptions.length > 0 && (
            <RecruiterSsoDropdown
              label="Tenant"
              icon={Building2}
              allLabel="All Tenants"
              itemNoun="tenants"
              emptyLabel="No tenants found"
              showDetail={false}
              accent="#7c3aed"
              recruiters={tenantPickerOptions(tenantOptions)}
              selectedEmails={selectedTenantIds}
              disabled={loading}
              onChange={handleTenantChange}
            />
          )}

          {/* Role / Position multi-select */}
          <div style={{ flex: "1 1 200px", minWidth: 170 }} ref={positionRef}>
            <div style={labelStyle}><FileText size={14} /> Role / Position</div>
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setPositionOpen((o) => !o)}
                disabled={loading}
                style={{
                  ...inp, display: "flex", alignItems: "center", justifyContent: "space-between",
                  cursor: "pointer", textAlign: "left",
                  borderColor: selectedPositions.size > 0 ? "rgba(0,65,194,0.5)" : "var(--ds-border)",
                  background: selectedPositions.size > 0 ? "rgba(0,65,194,0.07)" : "var(--ds-input-bg)",
                  padding: "8px 10px 8px 12px",
                }}
              >
                <span style={{ fontSize: 13, color: selectedPositions.size > 0 ? "#4D86E8" : "var(--ds-muted)", fontWeight: selectedPositions.size > 0 ? 600 : 400 }}>
                  {positionLabel}
                </span>
                <ChevronDown size={14} style={{ color: "var(--ds-muted)", flexShrink: 0, transition: "transform 0.18s", transform: positionOpen ? "rotate(180deg)" : "none" }} />
              </button>

              {positionOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 9999,
                  background: "var(--ds-card)", border: "1.5px solid var(--ds-border)",
                  borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", minWidth: "100%", maxWidth: 300,
                  maxHeight: 240, overflowY: "auto",
                }}>
                  {selectedPositions.size > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const cleared = new Set();
                        setSelectedPositions(cleared);
                        if (!dateRangeError) refreshAnalytics({ from, to, selectedPositions: cleared, selectedRecruiters });
                      }}
                      style={{ display: "block", width: "100%", padding: "8px 14px", background: "transparent", border: "none", borderBottom: "1px solid var(--ds-border)", cursor: "pointer", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ef4444", fontFamily: "inherit" }}
                    >
                      ✕ Clear selection
                    </button>
                  )}
                  {rolePositions.length === 0 ? (
                    <div style={{ padding: "12px 14px", color: "var(--ds-muted)", fontSize: 12 }}>No roles found</div>
                  ) : rolePositions.map((p) => {
                    const id = String(p.positionId);
                    const isChecked = selectedPositions.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          const next = new Set(selectedPositions);
                          isChecked ? next.delete(id) : next.add(id);
                          setSelectedPositions(next);
                          if (!dateRangeError) refreshAnalytics({ from, to, selectedPositions: next, selectedRecruiters });
                        }}
                        style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%",
                          padding: "9px 14px", background: isChecked ? "rgba(0,65,194,0.08)" : "transparent",
                          border: "none", borderBottom: "1px solid var(--ds-border)", cursor: "pointer",
                          textAlign: "left", fontFamily: "inherit", transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.background = "var(--ds-hover)"; }}
                        onMouseLeave={(e) => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}
                      >
                        <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isChecked ? "#1A5FD4" : "var(--ds-border)"}`, background: isChecked ? "#1A5FD4" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                          {isChecked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ds-text)", lineHeight: 1.3 }}>{p.positionName}</span>
                          {(p.createdBy || p.createdByEmail) && (
                            <div style={{ fontSize: 10, color: "var(--ds-muted)", marginTop: 2 }}>
                              Created by {p.createdBy || p.createdByEmail}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recruiter multi-select */}
          <RecruiterSsoDropdown
            recruiters={recruiters}
            selectedEmails={selectedRecruiters}
            disabled={loading}
            onChange={handleRecruiterChange}
          />

          {/* From date */}
          <div style={{ flex: "1 1 160px", minWidth: 140 }}>
            <div style={labelStyle}><Calendar size={14} /> From</div>
            <input
              type="date" value={from} max={fromMaxDate}
              onChange={(e) => handleFromChange(e.target.value)}
              style={{ ...inp, borderColor: (fromFieldError || dateRangeError) ? "#EF4444" : "var(--ds-border)" }}
            />
            {fromFieldError && <div style={{ color: "#EF4444", fontSize: 11, marginTop: 4 }}>{fromFieldError}</div>}
          </div>

          {/* To date */}
          <div style={{ flex: "1 1 160px", minWidth: 140 }}>
            <div style={labelStyle}><Calendar size={14} /> To</div>
            <input
              key={`to-min-${from}`} type="date" value={to} min={toMinDate} max={todayMaxDate}
              onChange={(e) => handleToChange(e.target.value)}
              style={{ ...inp, borderColor: (toFieldError || dateRangeError) ? "#EF4444" : "var(--ds-border)" }}
            />
            {toFieldError && <div style={{ color: "#EF4444", fontSize: 11, marginTop: 4 }}>{toFieldError}</div>}
          </div>

          {/* Refresh */}
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <button
              onClick={() => refreshAnalytics({ from, to, selectedPositions, selectedRecruiters })}
              disabled={loading || !!dateRangeError}
              style={{ ...btnPrimary, width: "100%", justifyContent: "center", opacity: (loading || !!dateRangeError) ? 0.6 : 1 }}
            >
              {loading
                ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%" }} className="ads-spin" /> Loading…</>
                : <><RefreshCw size={14} /> Refresh</>
              }
            </button>
          </div>

          {/* Preset buttons + Download */}
          <div style={{ flex: "1 1 340px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {presets.map((p) => (
              <button key={p.label} onClick={() => applyPreset(p.days)} disabled={loading} style={{ ...btnOutline, flex: "1 1 80px" }}>
                {p.label}
              </button>
            ))}
            <div style={{ position: "relative" }}>
              <button
                ref={exportBtnRef}
                onClick={() => {
                  if (exportOpen) {
                    setExportOpen(false);
                    return;
                  }
                  const btn = exportBtnRef.current;
                  if (btn) {
                    const rect = btn.getBoundingClientRect();
                    setExportMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                  }
                  setExportOpen(true);
                }}
                disabled={!data}
                title="Export or share report"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 14px", borderRadius: 8, fontFamily: "inherit",
                  fontSize: 12, fontWeight: 600, cursor: !data ? "not-allowed" : "pointer",
                  border: "1.5px solid rgba(0,65,194,0.35)",
                  background: exportOpen ? "rgba(0,65,194,0.14)" : "rgba(0,65,194,0.07)",
                  color: "#1A5FD4", transition: "all 0.18s",
                  opacity: !data ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { if (data) e.currentTarget.style.background = "rgba(0,65,194,0.14)"; }}
                onMouseLeave={(e) => { if (!exportOpen) e.currentTarget.style.background = "rgba(0,65,194,0.07)"; }}
              >
                <Download size={13} /> {downloading ? "Opening…" : "Export"}
                <ChevronDown size={12} style={{ transition: "transform 0.18s", transform: exportOpen ? "rotate(180deg)" : "none" }} />
              </button>
            </div>
          </div>
        </div>

        {dateRangeError && (
          <div style={{ marginTop: 14, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, padding: "10px 14px", color: "#F59E0B", fontSize: 13 }}>
            {dateRangeError}
          </div>
        )}
        {error && !dateRangeError && (
          <div style={{ marginTop: 14, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px 14px", color: "#EF4444", fontSize: 13 }}>
            {error}
          </div>
        )}
        {exportMsg && (
          <div style={{ marginTop: 14, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 8, padding: "10px 14px", color: "#059669", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <Globe size={14} /> {exportMsg}
          </div>
        )}
      </div>

      {/* ── KPI grid ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 16 }}>
          {(showAllKpis ? kpis : kpis.slice(0, KPI_VISIBLE_COUNT)).map((k) => (
            <KpiCard key={k.title} {...k} dateRange={dateRange} />
          ))}
        </div>
        {kpis.length > KPI_VISIBLE_COUNT && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button
              type="button"
              onClick={() => setShowAllKpis((prev) => !prev)}
              style={{ ...btnOutline, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {showAllKpis ? "Show less" : "Show more"}
              <ChevronDown size={14} style={{ transition: "transform 0.18s", transform: showAllKpis ? "rotate(180deg)" : "none" }} />
            </button>
          </div>
        )}
      </div>

      {/* ── Insights + Chart ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, position: "relative" }}>

        {/* Info popup overlay */}
        {infoOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) setInfoOpen(false); }}>
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: -1 }} />
            <div ref={infoRef} style={{ ...card, padding: 24, maxWidth: 540, width: "100%", position: "relative", maxHeight: "80vh", overflowY: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 15, color: "var(--ds-text)" }}>
                  <Info size={18} color="#F59E0B" /> What these metrics mean
                </div>
                <button onClick={() => setInfoOpen(false)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "var(--ds-input-bg)", color: "var(--ds-text2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", outline: "none" }}>
                  <X size={14} />
                </button>
              </div>
              <div style={{ color: "var(--ds-text2)", fontSize: 13, lineHeight: 1.8, display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  ["Job roles created", "Positions whose Created On date falls in the selected date range. With a User (SSO) filter, only positions created by that user are counted."],
                  ["Candidates Stack Ranked", "Total resumes processed through stack ranking in the date range (sum across all valid runs per position)."],
                  ["Resume scoring above 50%", "Resumes scoring 50%+ on each role's latest stack ranking run within the range."],
                  ["WhatsApp Invitation Sent", "Distinct (candidate, role) pairs who received the initial outreach template in the selected date range."],
                  ["Interested / Not Interested", "Among those invites — candidates whose latest status is Yes / No (or progressed past Yes)."],
                  ["No Response", "Among those invites — no Yes/No reply yet (Invitations − Interested − Not Interested)."],
                  ["Shortlisted", "Among those invites — marked Shortlisted by a recruiter."],
                  ["Overall response rate", "(Interested + Not Interested) ÷ WhatsApp invitations sent in the same date range (never above 100%)."],
                  ["User (SSO) filter", "All metrics are scoped to positions created by the selected user(s)."],
                ].map(([title, desc]) => (
                  <div key={title} style={{ ...card2, padding: "10px 14px", borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: "var(--ds-text)", fontSize: 12, marginBottom: 3 }}>{title}</div>
                    <div style={{ color: "var(--ds-text2)", fontSize: 12, lineHeight: 1.6 }}>{desc}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8, padding: "10px 14px", color: "var(--ds-text2)", fontSize: 12 }}>
                💡 Use <strong>Last 7 days</strong> for weekly reporting and <strong>Last 30 days</strong> for monthly review.
              </div>
            </div>
          </div>
        )}

        {/* Insights */}
        <div style={{ ...card, padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 900, color: "var(--ds-text)" }}>
              <Sparkles size={18} color="#F59E0B" /> Insights
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {dateRange && (
                <span style={{ background: "var(--ds-card2)", border: "1px solid var(--ds-border)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "var(--ds-text2)" }}>
                  {dateRange}
                </span>
              )}
              <button
                onClick={() => setInfoOpen(true)}
                title="What do these metrics mean?"
                style={{ width: 28, height: 28, borderRadius: "50%", border: "1.5px solid var(--ds-border)", background: "var(--ds-input-bg)", color: "#F59E0B", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", outline: "none", transition: "all 0.18s", flexShrink: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(245,158,11,0.12)"; e.currentTarget.style.borderColor = "#F59E0B"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "var(--ds-input-bg)"; e.currentTarget.style.borderColor = "var(--ds-border)"; }}
              >
                <Info size={13} />
              </button>
            </div>
          </div>
          <div style={{ color: "var(--ds-text2)", fontSize: 12, marginBottom: 14 }}>Derived from the summary counts above.</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ ...card2, padding: 14 }}>
              <div style={{ color: "var(--ds-text2)", fontSize: 11, fontWeight: 700 }}>Total candidate responses</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "var(--ds-text)", marginTop: 6 }}>{data ? totalResponses : "-"}</div>
              <div style={{ color: "var(--ds-muted)", fontSize: 11, marginTop: 4 }}>Interested + Not Interested</div>
            </div>
            <div style={{ ...card2, padding: 14 }}>
              <div style={{ color: "var(--ds-text2)", fontSize: 11, fontWeight: 700 }}>Avg resumes per run</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "var(--ds-text)", marginTop: 6 }}>{data ? avgResumesPerRun.toFixed(1) : "-"}</div>
              <div style={{ color: "var(--ds-muted)", fontSize: 11, marginTop: 4 }}>Total resumes ÷ Runs</div>
            </div>
            <div style={{ ...card2, padding: 14, gridColumn: "1 / -1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ color: "var(--ds-text2)", fontSize: 11, fontWeight: 700 }}>Interest rate (from responses)</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "var(--ds-text)", marginTop: 6 }}>{data ? `${interestRate.toFixed(1)}%` : "-"}</div>
                  <div style={{ color: "var(--ds-muted)", fontSize: 11, marginTop: 4 }}>Interested ÷ (Interested + Not Interested)</div>
                </div>
                <div style={{ flex: "1 1 160px", height: 10, borderRadius: 999, background: "var(--ds-card)", overflow: "hidden", marginTop: 8 }}>
                  <div style={{ width: `${Math.min(100, Math.max(0, interestRate))}%`, height: "100%", background: "linear-gradient(90deg,#16a34a,#22c55e)", transition: "width 0.5s" }} />
                </div>
              </div>
            </div>
            <div style={{ ...card2, padding: 14, gridColumn: "1 / -1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ color: "var(--ds-text2)", fontSize: 11, fontWeight: 700 }}>Overall response rate</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "var(--ds-text)", marginTop: 6 }}>{data ? `${overallResponseRatePct.toFixed(1)}%` : "-"}</div>
                  <div style={{ color: "var(--ds-muted)", fontSize: 11, marginTop: 4 }}>(Interested + Not Interested) ÷ Template recipients</div>
                </div>
                <div style={{ flex: "1 1 160px", height: 10, borderRadius: 999, background: "var(--ds-card)", overflow: "hidden", marginTop: 8 }}>
                  <div style={{ width: `${Math.min(100, Math.max(0, overallResponseRatePct))}%`, height: "100%", background: "linear-gradient(90deg,#7c3aed,#a78bfa)", transition: "width 0.5s" }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Chart */}
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontWeight: 900, color: "var(--ds-text)", marginBottom: 4, fontSize: 15 }}>Visual Overview</div>
          <div style={{ color: "var(--ds-text2)", fontSize: 11, marginBottom: 18 }}>Candidate response breakdown + activity summary</div>

          {/* Donut + legend */}
          <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 24 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <DonutChart
                size={130}
                thickness={20}
                slices={[
                  { value: interested, color: "#16a34a" },
                  { value: notInterested, color: "#ef4444" },
                  { value: noResponse, color: "var(--ds-border)" },
                ]}
              />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: "var(--ds-text)", lineHeight: 1 }}>{data ? totalResponses : "-"}</div>
                <div style={{ fontSize: 9, color: "var(--ds-muted)", fontWeight: 600, marginTop: 2 }}>REPLIES</div>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: "Interested", value: data ? interested : "-", color: "#16a34a", pct: totalResponses > 0 ? (interested / totalResponses * 100) : 0 },
                { label: "Not Interested", value: data ? notInterested : "-", color: "#ef4444", pct: totalResponses > 0 ? (notInterested / totalResponses * 100) : 0 },
                { label: "No Response", value: data ? noResponse : "-", color: "var(--ds-muted)", pct: invitationsSent > 0 ? (noResponse / invitationsSent * 100) : 0 },
              ].map((item) => (
                <div key={item.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: "var(--ds-text2)", fontWeight: 600 }}>{item.label}</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ds-text)" }}>{item.value}</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 999, background: "var(--ds-card)", overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, item.pct)}%`, height: "100%", background: item.color, transition: "width 0.5s ease", opacity: item.color === "var(--ds-muted)" ? 0.4 : 1 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Activity bars */}
          <div style={{ borderTop: "1px solid var(--ds-border)", paddingTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ds-text2)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Activity</div>
            {[
              { label: "JDs / Roles", value: data ? jdCount : 0, max: Math.max(jdCount, 1), color: "#ff6b35" },
              { label: "Candidates Stack Ranked", value: data ? candidatesSelfRanked : 0, max: Math.max(candidatesSelfRanked, 1), color: "#2f80ed" },
              { label: "WhatsApp Invitations Sent", value: data ? invitationsSent : 0, max: Math.max(invitationsSent, 1), color: "#f59e0b" },
              { label: "Resumes Scored >50%", value: data ? resumesScoredAboveFifty : 0, max: Math.max(resumesScoredAboveFifty, 1), color: "#27ae60" },
            ].map((item) => (
              <div key={item.label} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: "var(--ds-text2)", fontWeight: 600 }}>{item.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: item.color }}>{data ? item.value : "-"}</span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: "var(--ds-card)", overflow: "hidden" }}>
                  <div style={{ width: data ? "100%" : "0%", height: "100%", background: `linear-gradient(90deg, ${item.color}99, ${item.color})`, transition: "width 0.5s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      </>
      )}

      {exportOpen && exportMenuPos && typeof document !== "undefined" && createPortal(
        <div
          ref={exportMenuRef}
          style={{
            position: "fixed",
            top: exportMenuPos.top,
            right: exportMenuPos.right,
            zIndex: 99999,
            background: "var(--ds-card, #fff)",
            border: "1.5px solid var(--ds-border, #e5e7eb)",
            borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
            minWidth: 210,
          }}
        >
          {[
            { label: "Export to Excel",         Icon: FileText, accent: "#16a34a", action: exportToExcel },
            { label: "Export to Google Sheets", Icon: Globe,    accent: "#2f80ed", action: exportToGoogleSheets },
            { label: "Export to CSV",           Icon: Download, accent: "#7c3aed", action: exportToCSV },
            { label: "Export to HTML",          Icon: FileText, accent: "#f59e0b", action: exportToHTML },
            { label: "Email this report",       Icon: Mail,     accent: "#ef4444", action: emailReport },
          ].map(({ label, Icon, accent, action }, idx, arr) => (
            <button
              key={label}
              type="button"
              onClick={action}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "10px 14px", background: "transparent",
                border: "none", borderBottom: idx < arr.length - 1 ? "1px solid var(--ds-border, #e5e7eb)" : "none",
                cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                fontSize: 13, color: "var(--ds-text, #111827)", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-hover, #f3f4f6)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, background: `${accent}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon size={14} color={accent} />
              </div>
              <span style={{ fontWeight: 500 }}>{label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
